'use client'

/**
 * Private route error boundary
 *
 * Contains render/commit errors within the authenticated app (chat, spaces,
 * nexus, ...) so a transient failure (most notably a DOM reconciliation error
 * thrown during a streamed message's finalize commit) does NOT white-screen the
 * whole page via the app-level boundary.
 *
 * It attempts a bounded auto-recovery: the offending render (e.g. a message
 * finalizing) almost always re-renders cleanly, so we call reset() once
 * automatically. If it keeps failing, we fall back to a manual retry UI instead
 * of an infinite reset loop.
 *
 * Stale-deploy / missing-chunk errors cannot be fixed by reset(); they need a
 * hard reload to pick up the new build. That path runs before soft recovery.
 */

import { useEffect, useRef, useState } from 'react'

import { captureClientException } from '@/lib/analytics/capture-client-exception'
import {
  hardReloadForStaleDeploy,
  isLikelyStaleDeployError,
  tryRecoverFromStaleDeploy
} from '@/lib/utils/stale-deploy-recovery'

const MAX_AUTO_RECOVERIES = 2

export default function PrivateError({
  error,
  reset
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const autoRecoveriesRef = useRef(0)
  const [autoRecovering, setAutoRecovering] = useState(false)

  useEffect(() => {
    // ChunkLoadError / build-mismatch: soft reset() cannot fetch a deleted
    // chunk. Hard-reload once (cooldown-guarded) so Spaces/chat pick up the
    // fresh deploy instead of stranding on "This view hit a snag".
    if (isLikelyStaleDeployError(error)) {
      if (tryRecoverFromStaleDeploy()) {
        return
      }
      // Cooldown: capture and show manual retry (hard reload), not soft reset().
      captureClientException(error, {
        source: 'private-error-boundary',
        boundary: 'private-error-boundary',
        severity: 'error'
      })
      setAutoRecovering(false)
      return
    }

    captureClientException(error, {
      source: 'private-error-boundary',
      boundary: 'private-error-boundary',
      severity: 'error'
    })

    // Bounded auto-recovery: transient finalize/reconciliation crashes clear on
    // a fresh render. Retry automatically a couple of times before showing UI.
    if (autoRecoveriesRef.current < MAX_AUTO_RECOVERIES) {
      autoRecoveriesRef.current += 1
      setAutoRecovering(true)
      const id = window.setTimeout(() => {
        reset()
      }, 120)
      return () => window.clearTimeout(id)
    }

    setAutoRecovering(false)
  }, [error, reset])

  if (autoRecovering) {
    // Render nothing during the brief auto-recovery so there is no visible flash.
    return null
  }

  return (
    <div className="flex min-h-[240px] flex-col items-center justify-center gap-4 p-6 text-center">
      <h2 className="text-foreground text-lg font-semibold">This view hit a snag</h2>
      <p className="text-muted-foreground max-w-md text-sm">
        Something failed to render. Your data is safe. Retry to reload this view.
      </p>
      <button
        onClick={() => {
          if (isLikelyStaleDeployError(error)) {
            hardReloadForStaleDeploy()
            return
          }
          autoRecoveriesRef.current = 0
          reset()
        }}
        className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-2 text-sm font-medium"
      >
        Retry
      </button>
    </div>
  )
}
